import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Theme } from "@emotion/react";
import { Header3 } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import React, { FunctionComponent, ReactNode, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material";
import { layerSectionMixin, sidebarPadding, TypographyColorsEnum, styled, narrowPadding } from "@iventis/styles";
import { useTreeBrowser } from "@iventis/tree-browser";
import { canEdit, useFunctionality } from "@iventis/utilities";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { Node } from "@iventis/domain-model/model/node";
import { MappingEngine } from "@iventis/map-engine";
import content from "@iventis/translations/content";
import { ActionDropdownButton, PermissionsToolTipComponent } from "@iventis/components";
import { RootDispatch, RootState } from "./state/root.store";
import { sidebar } from "./state/sidebar.slice";
import { SidebarContent } from "./state/sidebar.slice.types";
import { SidebarButton } from "./sidebar-button";

const LayersNavComponent: FunctionComponent<{
    text: { [key: string]: string };
    theme: Theme;
    setContent: (content: SidebarContent) => void;
    mapNode: Node;
    handleOnAddLayer: (skipTemplateSelection: boolean) => void;
}> = ({ text, setContent, theme, mapNode, handleOnAddLayer }) => {
    const translate = useIventisTranslate();

    const dispatch: RootDispatch = useDispatch();

    const functionality = useFunctionality();

    const handleOnImportClicked = () => {
        if (functionality.importingLayers) {
            dispatch(sidebar.actions.showImportLayer(true));
        } else functionality.open("importingLayers");
    };

    const [{ isLoadingTree }] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "isLoadingTree");

    const isMapLoading = useSelector((state: RootState) => state.mapReducer.mapModule.mode)?.value === "loading";

    const disableButtons = isLoadingTree || isMapLoading;

    const canUserEdit = useMemo(() => canEdit(mapNode?.authorisation), [mapNode?.authorisation]);

    const { mappingEngine } = useSelector((s: RootState) => s.mapReducer);

    const handleDataTableButtonClick = () => {
        incrementMonitoringCount({ name: MetricName.DATA_TABLE_USED });
        setContent(SidebarContent.DATA_TABLE);
    };

    const handleChangeLogButtonClick = () => {
        incrementMonitoringCount({ name: MetricName.PLAN_CHANGE_LOG });
        setContent(SidebarContent.CHANGE_LOG);
    };

    const menuItems = [
        {
            displayName: translate(Content.map3.sidebarButtonTooltips.new_layer),
            onClick: () => handleOnAddLayer(false),
            primaryIcon: ["far", "layer-group"],
            testId: "create-layer-from-template",
        },
        {
            displayName: translate(Content.map3.layerTemplates.createCustomLayer),
            onClick: () => handleOnAddLayer(true),
            primaryIcon: ["far", "layer-plus"],
            testId: "create-custom-layer",
        },
        {
            displayName: translate(Content.map3.sidebarButtonTooltips.import_layer),
            onClick: handleOnImportClicked,
            primaryIcon: ["far", "file-import"],
            testId: "import-layer",
        },
    ];

    return (
        <>
            <StyledHeader color={TypographyColorsEnum.SUBDUED}>{text.layers}</StyledHeader>
            <div style={{ color: theme.primaryColors.subdued70 }}>
                <StyledNavItemsContainer>
                    {functionality.changeLog && (
                        <StyledSidebarButton
                            disable={disableButtons}
                            onClick={handleChangeLogButtonClick}
                            toolTip={translate(Content.map3.sidebarButtonTooltips.change_log)}
                            canUserEdit
                            testId="show-change-log-button"
                        >
                            <FontAwesomeIcon icon="clock-rotate-left" />
                        </StyledSidebarButton>
                    )}
                    <StyledSidebarButton
                        disable={disableButtons}
                        onClick={handleDataTableButtonClick}
                        toolTip={translate(Content.map3.sidebarButtonTooltips.attribute_data_table)}
                        canUserEdit
                        testId="show-data-table-button"
                    >
                        <FontAwesomeIcon icon="table" />
                    </StyledSidebarButton>
                    {/* Add layer button */}
                    {mappingEngine === MappingEngine.Mapbox && (
                        <ActionDropdownButton
                            clickHandler={() => handleOnAddLayer(false)}
                            variant="primary"
                            actionIcon={<FontAwesomeIcon size="xl" icon={{ prefix: "fas", iconName: "plus" }} />}
                            menuItems={menuItems}
                            disable={!canUserEdit || disableButtons}
                            primaryButtonTestId="new-layer-button"
                            secondaryButtonTestId="new-layer-button-dropdown"
                            tooltipPrimary={disableButtons ? null : canUserEdit ? translate(Content.common.buttons.createLayer) : <PermissionsToolTipComponent />}
                            tooltipSecondary={disableButtons ? null : canUserEdit ? translate(Content.map8.createLayerOptions) : <PermissionsToolTipComponent />}
                        />
                    )}
                </StyledNavItemsContainer>
            </div>
        </>
    );
};

const SidePanelSubContentTitle: FunctionComponent<{
    text: string;
    setContent: (content: SidebarContent) => void;
}> = ({ text, setContent }) => {
    const translate = useIventisTranslate();

    return (
        <>
            <StyledHeader>{text}</StyledHeader>
            <StyledSubContentActionButton
                testId="back-to-layers-button"
                onClick={() => setContent(SidebarContent.LAYERS)}
                toolTip={translate(content.map5.toolbarButtonTooltips.layers)}
                toolTipPlacement="bottom"
            >
                <StyledSubContentActionIcon icon={["far", "xmark"]} />
            </StyledSubContentActionButton>
        </>
    );
};

export const SidebarTopNavComponent: FunctionComponent<{
    content: SidebarContent;
    setContent: (content: SidebarContent) => void;
    mapNode: Node;
    handleOnAddLayer: (skipTemplateSelection: boolean) => void;
}> = ({ content, setContent, mapNode, handleOnAddLayer }) => {
    const theme = useTheme<Theme>();
    const translate = useIventisTranslate();
    const text = {
        layers: translate(Content.map.nav.layers),
        dataTable: translate(Content.map.nav.data_table),
        background: translate(Content.map.nav.background_maps),
    };

    let component: ReactNode;
    switch (content) {
        case SidebarContent.LAYERS:
            component = <LayersNavComponent text={text} setContent={setContent} theme={theme} mapNode={mapNode} handleOnAddLayer={handleOnAddLayer} />;
            break;
        case SidebarContent.DATA_TABLE:
            component = <SidePanelSubContentTitle text={text.dataTable} setContent={setContent} />;
            break;
        case SidebarContent.BACKGROUND_MAP:
            component = <SidePanelSubContentTitle text={text.background} setContent={setContent} />;
            break;
        default:
            component = "";
    }
    return <StyledNav>{component}</StyledNav>;
};

const StyledNav = styled.nav`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${narrowPadding} ${sidebarPadding};
    border-bottom: ${({ theme }) => `1px solid ${theme.shades.four}`};
    /* Sometimes the parent is a flex, sometimes it is a grid. To handle both situations for now, address both for heights */
    ${layerSectionMixin}

    button {
        height: 100%;
        :hover {
            color: ${({ theme }) => theme.primaryColors.focusAccent};
        }
    }
`;

const StyledSidebarButton = styled(SidebarButton)`
    padding: 0 15px;
`;

const StyledNavItemsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const StyledSubContentActionButton = styled(SidebarButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 15px;
    width: 15px;
    padding: 0;
`;

const StyledSubContentActionIcon = styled(FontAwesomeIcon)`
    height: 100%;
    width: 100%;
`;

const StyledHeader = styled(Header3)`
    text-transform: capitalize;
    font-weight: 500;
    color: ${({ theme }) => theme.typographyColors.core};
`;
